import type { FocusEvent, RefObject, InputHTMLAttributes, JSX, ReactNode } from 'react';
import { isValidElement, forwardRef, cloneElement, useRef } from 'react';
import { styled, css } from 'styled-components';
import { isNil, isNotNil, isRecord } from '@wistia/type-guards';
import { Icon } from '../Icon';
import { inputCss } from '../../css/inputCss';

const inputStyles = css`
  ${inputCss}
  input,
  textarea {
    padding: var(--wui-input-vertical-padding) var(--wui-input-horizontal-padding);
    background-color: var(--wui-input-color-bg);
    border: none;
    outline: 1px solid var(--wui-input-color-border);
    outline-offset: -1px;
    border-radius: var(--wui-input-border-radius);
    font-size: var(--wui-input-font-size);
    line-height: var(--wui-input-line-height);
    font-weight: var(--wui-input-font-weight);
    color: var(--wui-input-color-text);

    &:read-only {
      outline-style: dashed;
    }

    &::placeholder {
      color: var(--wui-input-placeholder);
      opacity: 1; /* Firefox */
    }

    &:focus {
      outline-width: 2px;
      outline-color: var(--wui-input-color-border-focus);
      outline-offset: -2px;
    }

    &[aria-invalid='true'] {
      outline-color: var(--wui-input-color-border-error);
    }

    &:disabled {
      outline-color: var(--wui-color-border-disabled);
      background-color: var(--wui-color-bg-surface-disabled);
    }
  }
`;

type StyledInputContainerProps = {
  $fullWidth: boolean;
  $fullHeight: boolean;
  $monospace: boolean;
  $isMultiline: boolean;
};

const calculateTextareaHeight = (rows = 1) =>
  `calc((var(--wui-input-line-height) * ${rows}) + (var(--wui-input-vertical-padding) * 2));`;

const StyledInputContainer = styled.div<StyledInputContainerProps>`
  display: inline-flex;
  position: relative;
  ${inputStyles};
  width: ${({ $fullWidth }) => ($fullWidth ? '100%' : 'auto')};
  height: ${({ $fullHeight, $isMultiline }) => ($fullHeight && $isMultiline ? '100%' : 'auto')};

  input,
  textarea {
    width: ${({ $fullWidth }) => ($fullWidth ? '100%' : 'auto')};
    ${({ $monospace }) => $monospace && 'font-family: var(--wui-typography-family-mono);'}
  }

  textarea {
    --wui-input-line-height: var(--wui-typography-body-3-line-height);

    border-radius: var(--wui-input-multiline-border-radius);
    height: ${({ $fullHeight }) => ($fullHeight ? '100%' : 'auto')};

    /* override height calculations when fullHeight is enabled */
    ${({ $fullHeight }) =>
      $fullHeight &&
      `
      &[rows='1'],
      &[rows='2'],
      &[rows='3'],
      &[rows='4'],
      &[rows='5'],
      &:not([rows]) {
        height: 100%;
        resize: none;
      }
    `}

    /* height based on rows attribute (1-5) - only when fullHeight is not enabled */
    ${({ $fullHeight }) =>
      !$fullHeight &&
      `
      &[rows='1'] {
        height: ${calculateTextareaHeight(1)};
      }

      &[rows='2'] {
        height: ${calculateTextareaHeight(2)};
      }

      &[rows='3'] {
        height: ${calculateTextareaHeight(3)};
      }

      &[rows='4'] {
        height: ${calculateTextareaHeight(4)};
      }

      &[rows='5'] {
        height: ${calculateTextareaHeight(5)};
      }

      /* make sure textareas without a rows attribute are not tiny */
      &:not([rows]) {
        height: ${calculateTextareaHeight(5)};
      }
    `}
  }

  [type='search'] {
    &::-webkit-search-cancel-button {
      appearance: none;
      height: 16px;
      width: 16px;
      margin-right: -4px;

      /* css vars don't work in data URLs, so the stroke color value is hardcoded. if we support dark mode, we'll need to swap the background image based on the users preferred color scheme */
      background-image: url("data:image/svg+xml,%3Csvg width='12' height='12' viewBox='0 0 16 16' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M12 4L4 12M4 4L12 12' stroke='%2364646a' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
      background-position: center;
      background-repeat: no-repeat;
      cursor: pointer;
      background-color: var(--wui-color-bg-surface-secondary);
      border-radius: var(--wui-border-radius-rounded);

      &:hover {
        background-color: var(--wui-color-bg-surface-secondary-hover);
      }
    }
  }

  .wui-input-left-icon {
    display: flex;
    align-items: center;
    position: absolute;
    top: 50%;
    left: var(--wui-input-horizontal-padding);
    transform: translateY(-50%);
  }

  &:has(.wui-input-left-icon) input,
  &:has(.wui-input-left-icon) textarea {
    padding-left: 32px;
  }

  .wui-input-right-icon {
    display: flex;
    align-items: center;
    position: absolute;
    top: 50%;
    right: var(--wui-input-horizontal-padding);
    transform: translateY(-50%);
  }

  &:has(.wui-input-right-icon) input,
  &:has(.wui-input-right-icon) textarea {
    padding-right: 32px;
  }
`;

export type InputProps = Omit<
  InputHTMLAttributes<HTMLInputElement | HTMLTextAreaElement>,
  'type'
> & {
  /**
   * When enabled, focusing the input will select the entire text within
   */
  autoSelect?: boolean;
  /**
   * Sets the validity status of the input
   */
  'aria-invalid'?: boolean;
  /**
   * Set the disabled state of the input
   */
  disabled?: boolean;
  /**
   * If true, the input will fill the height of its parent container
   * _note: only applicable when `type="multiline"`_
   */
  fullHeight?: boolean;
  /**
   * If true, the input will fill the width of its parent
   */
  fullWidth?: boolean;
  /**
   * If true, the input will use a monospace font
   */
  monospace?: boolean;
  /**
   * Icon to display on the left side of the input. Must be an instance of [Icon](?path=/docs/components-icon--docs)
   */
  leftIcon?: ReactNode | undefined;
  /**
   * Icon to display on the right side of the input. Must be an instance of [Icon](?path=/docs/components-icon--docs)
   */
  rightIcon?: ReactNode | undefined;
  /**
   * The type of the input
   */
  type?: 'email' | 'multiline' | 'number' | 'password' | 'phone' | 'search' | 'text' | 'url';
};

/**
 * Capture user input with a text field. Should be used within a [Form]() and [FormField]().
 */
export const Input = forwardRef<HTMLInputElement | HTMLTextAreaElement, InputProps>(
  (
    {
      fullWidth = true,
      fullHeight = false,
      monospace = false,
      type = 'text',
      autoSelect = false,
      leftIcon,
      rightIcon,
      ...props
    }: InputProps,
    externalRef,
  ): JSX.Element => {
    const internalRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);

    // TODO - mergeRefs is not working for this for some reason
    const ref =
      isNotNil(externalRef) && isRecord(externalRef) && 'current' in externalRef
        ? (externalRef as RefObject<HTMLInputElement | HTMLTextAreaElement | null>)
        : internalRef;

    let leftIconToDisplay = leftIcon;

    if (isNil(leftIcon) && type === 'search') {
      leftIconToDisplay = <Icon type="search" />;
    }

    if (isNotNil(leftIconToDisplay) && isValidElement(leftIconToDisplay)) {
      leftIconToDisplay = cloneElement(leftIconToDisplay, {
        size: 'md',
        className: 'wui-input-left-icon',
      } as Partial<{ size: string; className: string }>);
    }

    let rightIconToDisplay = rightIcon;

    if (isNotNil(rightIconToDisplay) && isValidElement(rightIconToDisplay)) {
      rightIconToDisplay = cloneElement(rightIconToDisplay, {
        size: 'md',
        className: 'wui-input-right-icon',
      } as Partial<{ size: string; className: string }>);
    }

    const handleFocus = <T extends HTMLInputElement | HTMLTextAreaElement>(
      event: FocusEvent<T>,
    ) => {
      if (isNotNil(props.onFocus)) {
        props.onFocus(event);
      }
      if (autoSelect && type !== 'multiline' && ref.current instanceof HTMLInputElement) {
        requestAnimationFrame(() => {
          ref.current?.select();
        });
      }
    };

    return (
      <StyledInputContainer
        $fullHeight={fullHeight}
        $fullWidth={fullWidth}
        $isMultiline={type === 'multiline'}
        $monospace={monospace}
        data-wui-input-container={true}
      >
        {leftIconToDisplay ?? null}
        {type === 'multiline' ? (
          <textarea
            {...props}
            ref={ref as unknown as RefObject<HTMLTextAreaElement | null>}
            onFocus={handleFocus<HTMLTextAreaElement>}
          />
        ) : (
          <input
            {...props}
            ref={ref as unknown as RefObject<HTMLInputElement | null>}
            onFocus={handleFocus<HTMLInputElement>}
            type={type}
          />
        )}
        {rightIconToDisplay ?? null}
      </StyledInputContainer>
    );
  },
);

Input.displayName = 'Input';
